/**
 * Centralized, future-ready navigation config for the Layout sidebar.
 *
 * Structure:
 *   - Top-level array of GROUPS. The first entry with `kind: "primary"` is
 *     rendered as a single non-collapsible item (Dashboard).
 *   - Each group has a label, optional icon, and an `items` array.
 *   - Each item supports: { to, label, icon, allowedRoles, badge?, condition?(user) }
 *
 * Role filtering is centralized in `filterNavForUser(nav, user, helpers)` —
 * components never check roles themselves. Adding a new module means appending
 * one entry to this file.
 */
import {
  LayoutDashboard, Trophy, ClipboardList, Users, Building2,
  Shield, FileSearch, ClipboardCheck, FileBarChart, FileText, User,
  Users as UsersRound, Sliders, Sparkles, UserPlus, BookOpenText,
} from "lucide-react";

export const NAV_CONFIG = [
  {
    kind: "primary",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard,
        allowedRoles: ["CEO", "Project Manager", "Administration", "House Manager"] },
    ],
  },
  {
    kind: "group", id: "operations", label: "Operations",
    description: "Daily operational workflows",
    items: [
      { to: "/score-entry", label: "Score Entry", icon: ClipboardList,
        allowedRoles: ["CEO", "Project Manager", "Administration", "House Manager"] },
      { to: "/qa/entry", label: "QA Audit", icon: ClipboardCheck,
        allowedRoles: ["CEO", "Project Manager", "Administration", "House Manager"] },
      { to: "/qa/dashboard", label: "QA Dashboard", icon: FileBarChart,
        allowedRoles: ["CEO", "Project Manager", "Administration", "House Manager"] },
      { to: "/staff", label: "Staff Directory", icon: Users,
        allowedRoles: ["CEO", "Project Manager", "Administration", "House Manager"] },
      { to: "/homes", label: "Group Homes", icon: Building2,
        allowedRoles: ["CEO", "Project Manager", "Administration"] },
    ],
  },
  {
    kind: "group", id: "reporting", label: "Reporting & Analytics",
    description: "Leadership reporting and KPI oversight",
    items: [
      { to: "/reports", label: "Reports", icon: FileText,
        allowedRoles: ["CEO", "Project Manager", "Administration", "House Manager"] },
      { to: "/leaderboard", label: "Leaderboard", icon: Trophy,
        allowedRoles: ["CEO", "Project Manager", "Administration", "House Manager"] },
      { to: "/kpi-weights", label: "KPI Weights", icon: Sliders,
        allowedRoles: ["CEO", "Project Manager"] },
      { to: "/audit-logs", label: "Audit Logs", icon: FileSearch,
        allowedRoles: ["CEO", "Project Manager", "Administration"] },
    ],
  },
  {
    kind: "group", id: "administration", label: "Administration",
    description: "System and access management",
    items: [
      { to: "/users", label: "Users & Access", icon: UsersRound,
        allowedRoles: ["CEO", "Project Manager", "Administration"] },
      { to: "/permissions", label: "Permission Matrix", icon: Shield,
        allowedRoles: ["CEO", "Project Manager"] },
      { to: "/auth-repair", label: "Auth Repair", icon: Shield,
        allowedRoles: ["CEO", "Project Manager"] },
      // Staff Onboarding intentionally lives inside /users; surface it here too
      // for discoverability but reuse the same query-based deep link.
      { to: "/users?onboard=1", label: "Staff Onboarding", icon: UserPlus,
        allowedRoles: ["CEO", "Project Manager", "Administration"],
        // House Managers reach this through the /users page button when granted
        // the User Management/Create capability — kept off the primary nav.
        condition: (user, h) => ["CEO", "Project Manager", "Administration"].includes(user.access_role)
                                || (user.access_role === "House Manager" && h.hasCapability("User Management", "Create")),
      },
    ],
  },
  {
    kind: "group", id: "policies", label: "Policy Library",
    description: "Internal policies and procedures",
    items: [
      { to: "/policies", label: "Policy Library", icon: BookOpenText,
        allowedRoles: ["CEO", "Project Manager", "COO", "Administration", "House Manager"],
        condition: (user, h) => h.hasCapability("Policy Library", "Access") },
    ],
  },
  {
    kind: "group", id: "ai", label: "AI Tools",
    description: "AI-assisted helpers",
    items: [
      { to: "/ai/notes", label: "AI Note Assistant", icon: Sparkles,
        allowedRoles: ["CEO", "Project Manager", "Administration", "House Manager"] },
    ],
  },
];

// Staff role gets a small standalone tree (no groups).
export const STAFF_NAV = [
  { kind: "primary", items: [
    { to: "/me", label: "My Dashboard", icon: LayoutDashboard, allowedRoles: ["Staff"] },
    { to: "/me/history", label: "My Score History", icon: ClipboardList, allowedRoles: ["Staff"] },
    { to: "/leaderboard", label: "Leaderboard", icon: Trophy, allowedRoles: ["Staff"] },
    { to: "/policies", label: "Policy Library", icon: BookOpenText, allowedRoles: ["Staff"],
      condition: (user, h) => h.hasCapability("Policy Library", "Access") },
    { to: "/ai/notes", label: "AI Note Assistant", icon: Sparkles, allowedRoles: ["Staff"] },
  ]},
];

/**
 * Apply role + capability filtering to the nav config.
 * `helpers` carries `hasCapability(module, capability)` from the auth context.
 * Returns a structurally identical config with empty groups removed.
 */
export function filterNavForUser(nav, user, helpers = {}) {
  const role = user?.access_role;
  if (!role) return [];
  return nav
    .map((group) => ({
      ...group,
      items: (group.items || []).filter((item) => {
        if (item.allowedRoles && !item.allowedRoles.includes(role)) return false;
        if (typeof item.condition === "function" && !item.condition(user, helpers)) return false;
        return true;
      }),
    }))
    .filter((group) => group.items.length > 0);
}
